import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, IconButton, Grid, useTheme, useMediaQuery, Menu, MenuItem, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import PatientRow from '../../../UIControls/PatientRow';
import AddPatientDialog from './add-patient-modal';
import { FeedbackCTA } from '../../feedback-cta';
import WhiteboardCard from './WhiteboardCard';
import CustomisedHandoutCard from '../customise-mode/CustomisedHandoutCard';
import GradientBanner from '../../../UIControls/GradientBanner';
import { archiveWhiteboard } from '../../../helper/whiteboardUtils';
import { archiveCustomisedHandout } from '../../../helper/customisedHandoutUtils';
import PinkGradientButtonWithArrow from '../../../UIControls/PinkGradientButtonWithArrow';

const MyClinicWhiteboards = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { patientId } = useParams();
    const { userDataStore, whiteboardStore, authStore, imageStore, customisedHandoutStore } = useContext(StoreContext);
    const [whiteboards, setWhiteboards] = useState([]);
    const [customisedHandouts, setCustomisedHandouts] = useState([]);
    const [combinedItems, setCombinedItems] = useState([]); // Combined whiteboards and customised handouts
    const [filteredWhiteboards, setFilteredWhiteboards] = useState([]);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortBy, setSortBy] = useState('RECENT');
    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' = Newest first
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [filterType, setFilterType] = useState('customisedHandouts'); // 'all', 'whiteboards', 'customisedHandouts'

    const [patient, setPatient] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();

    const paginatedWhiteboards = filteredWhiteboards.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    // Helper function to get image source for customised handout
    const getCustomisedHandoutImageSrc = (customisedHandout) => {
        // First check if there's an attached whiteboard with canvas image link
        if (customisedHandout.attachedWhiteboardCanvasImageLink) {
            return customisedHandout.attachedWhiteboardCanvasImageLink;
        }

        // Extract first illustration from effectiveContentData
        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];
        if (!contentData || !Array.isArray(contentData)) {
            return null;
        }

        // Find first illustration block
        for (const section of contentData) {
            if (section.blocks) {
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) {
                    return illustrationBlock.src;
                }
            }
        }

        return null;
    };

    useEffect(() => {
        const stored = sessionStorage.getItem('myClinicSelectedPatient');
        if (stored) {
            setPatient(JSON.parse(stored));
            sessionStorage.removeItem('myClinicSelectedPatient');
        }
    }, []);

    useEffect(() => {
        if (!whiteboardStore.isInitialized) return;

        let whiteboardsData = Object.values(whiteboardStore.whiteboardData);

        // console.log('Updating Clinic Whiteboards State');

        // All whiteboards for this patient, excluding archived
        whiteboardsData = whiteboardsData
            .filter((wb) => wb.whiteboardStatus !== 'archived' && wb.patientId === patientId)
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setWhiteboards(whiteboardsData);
        setFilteredWhiteboards(whiteboardsData);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized, patientId]);

    // Load customised handouts for this patient
    useEffect(() => {
        if (!customisedHandoutStore?.isInitialized) return;

        let customisedHandoutsData = Object.values(customisedHandoutStore.customisedHandoutData);

        // All customised handouts for this patient, excluding archived
        customisedHandoutsData = customisedHandoutsData
            .filter((ch) => ch.customisedStatus === 'active' && ch.patientId === patientId)
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setCustomisedHandouts(customisedHandoutsData);
    }, [customisedHandoutStore?.customisedHandoutData, customisedHandoutStore?.isInitialized, patientId]);

    // Combine whiteboards and customised handouts
    useEffect(() => {
        let combined = [
            ...whiteboards.map((wb) => ({ ...wb, type: 'whiteboard' })),
            ...customisedHandouts.map((ch) => ({ ...ch, type: 'customisedHandout' })),
        ].sort((a, b) => b.lastUpdated - a.lastUpdated);

        setCombinedItems(combined);

        // Apply filter
        if (filterType === 'whiteboards') {
            combined = combined.filter((item) => item.type === 'whiteboard');
        } else if (filterType === 'customisedHandouts') {
            combined = combined.filter((item) => item.type === 'customisedHandout');
        }

        setFilteredWhiteboards(combined);
        setPage(0); // Reset to first page when filter changes
    }, [whiteboards, customisedHandouts, filterType]);

    useEffect(() => {
        // Fetch image previews for both whiteboards and customised handouts
        const fetchImages = async () => {
            const images = {};
            for (const item of filteredWhiteboards) {
                if (item.type === 'whiteboard' && item.canvasImageLink) {
                    const image = await imageStore.getImageFromIndexedDB(item.canvasImageLink);
                    if (image?.data) images[item.canvasImageLink] = image.data;
                } else if (item.type === 'customisedHandout') {
                    // Handle customised handout images
                    const illustrationSrc = getCustomisedHandoutImageSrc(item);
                    if (illustrationSrc) {
                        const image = await imageStore.getImageFromIndexedDB(illustrationSrc);
                        if (image?.data) images[illustrationSrc] = image.data;
                    }
                }
            }
            setImageMap(images);
        };
        fetchImages();
    }, [filteredWhiteboards]);

    const handleSearchInputChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);

        const filtered = combinedItems.filter((item) => {
            if (item.type === 'whiteboard') {
                return (
                    item.handoutContentName?.toLowerCase().includes(value) ||
                    item.handoutAlphabeticalName?.toLowerCase().includes(value) ||
                    item.handoutDocumentTitle?.toLowerCase().includes(value) ||
                    item.notes?.toLowerCase().includes(value) ||
                    item.fileTitle?.toLowerCase().includes(value) ||
                    item.patientName?.toLowerCase().includes(value)
                );
            } else if (item.type === 'customisedHandout') {
                // Extract document title from effectiveContentData for search
                const locale = 'en-UK';
                const contentData = item.effectiveContentData?.[locale];
                let documentTitle = '';
                if (contentData && Array.isArray(contentData) && contentData.length > 0) {
                    const firstSection = contentData[0];
                    if (firstSection && firstSection.blocks) {
                        const titleBlock = firstSection.blocks.find((block) => block.type === 'documentTitle');
                        documentTitle = titleBlock?.value || '';
                    }
                }
                return documentTitle.toLowerCase().includes(value) || item.notes?.toLowerCase().includes(value);
            }
            return false;
        });

        setFilteredWhiteboards(filtered);
        setPage(0);
    };

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filteredWhiteboards.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const columnWidths = {
        patientName: 2,
        patientSurname: 2,
        patientEmail: 3,
        clinicPatientId: 1,
        patientStatus: 1,
        actions: 1,
        whiteboards: 2,
    };

    const handleCloseMenu = (option) => {
        const whiteboardFavouriteIds = userDataStore.userData.whiteboardsFavourites || [];
        const customisedHandoutFavouriteIds = userDataStore.userData.customisedHandoutsFavourites || [];

        if (option === 'RECENT') {
            const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
            setSortDirection(newDirection);
            setHasSortedRecently(true);
            setSortBy('RECENT');

            // Sort the current filtered items (not just whiteboards)
            const sorted = [...filteredWhiteboards].sort((a, b) =>
                newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
            );
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            const newDirection = isSameSort && sortDirection === 'desc' ? 'asc' : 'desc';

            setSortDirection(newDirection);
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');

            // Sort by favourites - include both whiteboards and customised handouts
            const favouritesGroup = filteredWhiteboards
                .filter((item) => {
                    if (item.type === 'whiteboard') {
                        return whiteboardFavouriteIds.includes(item.whiteboardId);
                    } else if (item.type === 'customisedHandout') {
                        return customisedHandoutFavouriteIds.includes(item.customisedId);
                    }
                    return false;
                })
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const nonFavouritesGroup = filteredWhiteboards
                .filter((item) => {
                    if (item.type === 'whiteboard') {
                        return !whiteboardFavouriteIds.includes(item.whiteboardId);
                    } else if (item.type === 'customisedHandout') {
                        return !customisedHandoutFavouriteIds.includes(item.customisedId);
                    }
                    return true;
                })
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const sorted = [...favouritesGroup, ...nonFavouritesGroup];
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        setAnchorEl(null);
    };

    const handleArchiveWhiteboard = (wb) =>
        archiveWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setWhiteboards,
            setFilteredWhiteboards,
            showSnackbar,
        );

    const handleToggleFavourite = async (whiteboardId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.whiteboardsFavourites?.includes(whiteboardId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.whiteboardsFavourites.filter((id) => id !== whiteboardId)
            : [...(userDataStore.userData.whiteboardsFavourites || []), whiteboardId];

        if (!isFavourite && userDataStore.userData.whiteboardsFavourites.length >= 30) {
            showSnackbar('You can only mark up to 30 whiteboards as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            whiteboardsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            whiteboardsFavourites: updatedFavourites,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update favourites', err);
        }
    };

    const handleEditWhiteboard = (whiteboard) => {
        if (!whiteboard.handoutId) {
            console.error('Missing handoutContentId for whiteboard:', whiteboard);
            return;
        }

        // Set whiteboard state in localStorage so it's loaded on Handout page
        localStorage.setItem(
            'lastWhiteboardState',
            JSON.stringify({
                whiteboardId: whiteboard.whiteboardId,
                handoutId: whiteboard.handoutId,
                canvasImageLink: whiteboard.canvasImageLink,
                canvasJsonLink: whiteboard.canvasJsonLink,
                fileName: whiteboard.fileName || '',
                eTag: whiteboard.eTag,
                ...(whiteboard.lastUsedColours && { lastUsedColours: whiteboard.lastUsedColours }),
                ...(whiteboard.updatedByFirstName && {
                    updatedByFirstName: whiteboard.updatedByFirstName,
                }),
                ...(whiteboard.updatedByLastName && {
                    updatedByLastName: whiteboard.updatedByLastName,
                }),
                ...(whiteboard.diagramVariant && { diagramVariant: whiteboard.diagramVariant }),
            }),
        );

        sessionStorage.setItem('autoloadWhiteboardFromLibrary', 'true');
        // pass details for disclaimer
        sessionStorage.setItem(
            'whiteboardDoctorName',
            `${whiteboard.updatedByFirstName || ''} ${whiteboard.updatedByLastName || ''}`.trim(),
        );
        sessionStorage.setItem('whiteboardClinicName', userDataStore?.userData?.clinicName || '');
        // Navigate to the handout page
        navigate(`/handout/${whiteboard.handoutId}`);
    };

    // Customised Handout Handlers
    const handleToggleFavouriteCustomisedHandout = async (customisedId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.customisedHandoutsFavourites?.includes(customisedId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.customisedHandoutsFavourites.filter((id) => id !== customisedId)
            : [...(userDataStore.userData.customisedHandoutsFavourites || []), customisedId];

        if (!isFavourite && (userDataStore.userData.customisedHandoutsFavourites?.length || 0) >= 30) {
            showSnackbar('You can only mark up to 30 customised handouts as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            customisedHandoutsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            customisedHandoutsFavourites: updatedFavourites,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update customised handout favourites', err);
        }
    };

    const handleEditCustomisedHandout = (customisedHandout) => {
        if (!customisedHandout.baseContentId) {
            console.error('Missing baseContentId for customised handout:', customisedHandout);
            showSnackbar('Cannot edit: missing base content ID', 'error');
            return;
        }

        // Store CH ID in localStorage (similar to whiteboard pattern)
        localStorage.setItem('loadCustomisedHandout', customisedHandout.customisedId);

        console.log('Navigating to edit customised handout:', customisedHandout.customisedId);

        // Navigate to base handout
        navigate(`/handout/${customisedHandout.baseContentId}`);
    };

    const handleArchiveCustomisedHandout = (customisedHandout) =>
        archiveCustomisedHandout(
            customisedHandout,
            userDataStore,
            customisedHandoutStore,
            authStore,
            setCustomisedHandouts,
            setFilteredWhiteboards,
            showSnackbar,
        );

    const handleEditPatient = (p) => {
        setEditingPatient(p);
        setEditDialogOpen(true);
    };

    const handleArchive = async (patientId) => {
        try {
            const updatedPatient = {
                ...patient,
                patientStatus: 'archived',
            };

            await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);

            setPatient(updatedPatient);

            showSnackbar('Patient archived');
        } catch (err) {
            showSnackbar('Failed to archive patient', 'error');
        }
    };

    const columns = isMobileOrTablet
        ? []
        : [
              { key: 'patientName', label: 'Name' },
              { key: 'patientSurname', label: 'Surname' },
              { key: 'patientEmail', label: 'Email' },
              { key: 'clinicPatientId', label: 'PMS ID' },
              { key: 'patientStatus', label: 'Status' },
              { key: 'actions', label: 'Operation' },
          ];

    return (
        <>
            {/* Gradient Header */}
            <GradientBanner
                title="My Clinic"
                subtitle="Overview of patient's handouts"
                placeholder="Search handouts..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isMobileOrTablet}
                height="auto"
                paddingY={isMobile ? '48px' : '64px'}
                paddingBottom={isMobile ? '40px' : '64px'}
                className="search-banner"
            />

            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
                // className="clinic-table"
            >
                <Box sx={{ width: '100%', margin: '0 auto', mt: 4, position: 'relative' }}>
                    {patient && (
                        <Box
                            component="section"
                            mx={3}
                            sx={{ backgroundColor: 'white', borderRadius: 2, overflowX: 'auto' }}
                        >
                            {!isMobileOrTablet && (
                                <Grid container sx={{ height: '60px', px: 2, minWidth: 1100 }}>
                                    {columns.map((col) => (
                                        <Grid
                                            item
                                            key={col.key}
                                            xs={columnWidths[col.key] || 1}
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            <Typography>{col.label}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            <PatientRow
                                key={patient.id}
                                patient={patient}
                                onEdit={handleEditPatient}
                                onArchive={handleArchive}
                                hasWhiteboard={true}
                                onView={() => {}}
                                hideWhiteboardColumn={true}
                                customColumnWidths={columnWidths}
                                compactStatus={true}
                                isMobileOrTablet={isMobileOrTablet}
                            />
                        </Box>
                    )}
                    {/* Back Button */}
                    <Box sx={{ my: 4, px: 3 }}>
                        <PinkGradientButtonWithArrow
                            label="Back to My Patients"
                            onClick={() => {
                                navigate('/my-clinic');
                            }}
                            arrowPosition="left"
                        />
                    </Box>

                    {/* Filter and Sort Controls */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent:
                                userDataStore?.subscriptionData?.accountType === 'Student'
                                    ? 'flex-end'
                                    : 'space-between',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            mt: 2,
                            pr: isMobile ? 0 : 2,
                            mb: isMobile ? 2 : '67px',
                            paddingX: isMobile ? '48px' : '66px',
                            gap: isMobile ? 1.5 : 2,
                        }}
                    >
                        {/* Filter Radio Buttons - Hidden for Student plan */}
                        {userDataStore?.subscriptionData?.accountType !== 'Student' && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: isMobile ? 1.5 : 3,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    width: isMobile ? '100%' : 'auto',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: isMobile ? '12px' : '14px',
                                        lineHeight: '14px',
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        color: '#1919194D',
                                        flexShrink: 0,
                                    }}
                                >
                                    SHOW:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: isMobile ? 1.5 : 2,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box
                                        onClick={() => setFilterType('all')}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? 14 : 16,
                                                height: isMobile ? 14 : 16,
                                                borderRadius: '50%',
                                                border: '2px solid #000453',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: filterType === 'all' ? '#000453' : 'transparent',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {filterType === 'all' && (
                                                <Box
                                                    sx={{
                                                        width: isMobile ? 6 : 8,
                                                        height: isMobile ? 6 : 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: filterType === 'all' ? '#000453' : '#1919194D',
                                                fontWeight: filterType === 'all' ? 600 : 500,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            All
                                        </Typography>
                                    </Box>

                                    <Box
                                        onClick={() => setFilterType('whiteboards')}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? 14 : 16,
                                                height: isMobile ? 14 : 16,
                                                borderRadius: '50%',
                                                border: '2px solid #000453',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor:
                                                    filterType === 'whiteboards' ? '#000453' : 'transparent',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {filterType === 'whiteboards' && (
                                                <Box
                                                    sx={{
                                                        width: isMobile ? 6 : 8,
                                                        height: isMobile ? 6 : 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: filterType === 'whiteboards' ? '#000453' : '#1919194D',
                                                fontWeight: filterType === 'whiteboards' ? 600 : 500,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Whiteboards
                                        </Typography>
                                    </Box>

                                    <Box
                                        onClick={() => setFilterType('customisedHandouts')}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? 14 : 16,
                                                height: isMobile ? 14 : 16,
                                                borderRadius: '50%',
                                                border: '2px solid #000453',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor:
                                                    filterType === 'customisedHandouts' ? '#000453' : 'transparent',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {filterType === 'customisedHandouts' && (
                                                <Box
                                                    sx={{
                                                        width: isMobile ? 6 : 8,
                                                        height: isMobile ? 6 : 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter',
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: filterType === 'customisedHandouts' ? '#000453' : '#1919194D',
                                                fontWeight: filterType === 'customisedHandouts' ? 600 : 500,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Customised Handouts
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Sort By Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                            }}
                        >
                            {/* Sort by */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    m: 0,
                                }}
                            >
                                {/* Sort By Text */}
                                <Typography
                                    onClick={handleOpenMenu}
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        lineHeight: '14px',
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        color: '#1919194D',
                                        cursor: 'pointer',
                                    }}
                                >
                                    SORT BY{' '}
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#000453',
                                        }}
                                    >
                                        {sortBy}
                                    </Typography>
                                </Typography>

                                {/* Dropdown Icon */}
                                <IconButton onClick={handleOpenMenu} sx={{ ml: 1, p: 0 }}>
                                    {sortBy === 'RECENT' && hasSortedRecently ? (
                                        sortDirection === 'desc' ? (
                                            <ExpandMoreIcon sx={{ color: '#000453' }} />
                                        ) : (
                                            <ExpandLessIcon sx={{ color: '#000453' }} />
                                        )
                                    ) : (
                                        <ExpandMoreIcon sx={{ color: '#000453' }} />
                                    )}
                                </IconButton>

                                {/* Dropdown Menu */}
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => handleCloseMenu(null)}
                                >
                                    <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                                        RECENT
                                    </MenuItem>
                                    <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                                        FAVOURITES
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                    </Box>
                    {filteredWhiteboards.length === 0 ? (
                        <Box sx={{ mt: 6, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#000453' }}>
                                No handouts available yet.
                            </Typography>

                            <Typography variant="body1" sx={{ mt: 1, color: '#000453' }}>
                                To start a whiteboard session, select your handout of choice and then toggle on
                                whiteboard.
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#000453' }}>
                                Annotate our illustration and then click save. Once saved your whiteboard will appear
                                here.
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2, color: '#000453' }}>
                                To create a customised handout, open any handout and toggle on Customise mode.
                            </Typography>
                            {/* Button with spacing */}
                            <Box
                                sx={{
                                    mt: 3,
                                    mb: 3,
                                    display: 'flex',
                                    justifyContent: { xs: 'center', sm: 'flex-end' },
                                }}
                            >
                                <PinkGradientButtonWithArrow
                                    label="Search Handouts"
                                    onClick={() => navigate('/search')}
                                />
                            </Box>
                        </Box>
                    ) : (
                        // Combined Whiteboards and Customised Handouts
                        <Grid
                            container
                            spacing={3}
                            px={isMobile ? '48px' : '66px'}
                            sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                        >
                            {paginatedWhiteboards.map((item, index) => {
                                if (item.type === 'whiteboard') {
                                    return (
                                        <WhiteboardCard
                                            key={item.whiteboardId || index}
                                            whiteboard={item}
                                            isMobile={isMobile}
                                            userDataStore={userDataStore}
                                            authStore={authStore}
                                            whiteboardStore={whiteboardStore}
                                            showSnackbar={showSnackbar}
                                            handleToggleFavourite={handleToggleFavourite}
                                            handleEditWhiteboard={handleEditWhiteboard}
                                            handleArchiveWhiteboard={handleArchiveWhiteboard}
                                            imageMap={imageMap}
                                            index={index}
                                        />
                                    );
                                } else if (item.type === 'customisedHandout') {
                                    return (
                                        <CustomisedHandoutCard
                                            key={item.customisedId || index}
                                            customisedHandout={item}
                                            isMobile={isMobile}
                                            userDataStore={userDataStore}
                                            showSnackbar={showSnackbar}
                                            handleToggleFavourite={handleToggleFavouriteCustomisedHandout}
                                            handleEditCustomisedHandout={handleEditCustomisedHandout}
                                            handleArchiveCustomisedHandout={handleArchiveCustomisedHandout}
                                            imageMap={imageMap}
                                            index={index}
                                        />
                                    );
                                }
                                return null;
                            })}

                            {/* Pagination Controls */}
                            {filteredWhiteboards.length > 6 && (
                                <>
                                    <IconButton
                                        onClick={handlePrevPage}
                                        disabled={page === 0}
                                        sx={{
                                            position: 'absolute',
                                            left: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 10,
                                            backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            '&:hover': { backgroundColor: 'rgba(0, 4, 83, 0.12)' },
                                        }}
                                    >
                                        <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                    </IconButton>

                                    <IconButton
                                        onClick={handleNextPage}
                                        disabled={(page + 1) * whiteboardsPerPage >= filteredWhiteboards.length}
                                        sx={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 10,
                                            backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            '&:hover': { backgroundColor: 'rgba(0, 4, 83, 0.12)' },
                                        }}
                                    >
                                        <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                    </IconButton>
                                </>
                            )}
                        </Grid>
                    )}

                    {editingPatient && (
                        <AddPatientDialog
                            open={editDialogOpen}
                            onClose={() => setEditDialogOpen(false)}
                            showSnackbar={showSnackbar}
                            onSubmit={async (updatedData) => {
                                try {
                                    const updatedPatient = {
                                        ...editingPatient,
                                        ...updatedData,
                                        patientId: editingPatient.patientId,
                                    };

                                    await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);

                                    setPatient(updatedPatient);

                                    showSnackbar('Patient updated!', 'success');
                                } catch (error) {
                                    console.error('Failed to update patient:', error);
                                    showSnackbar('Failed to update patient.', 'error');
                                } finally {
                                    setEditDialogOpen(false);
                                }
                            }}
                            initialValues={editingPatient}
                        />
                    )}
                </Box>
                {/* New Feedback Section */}
                <FeedbackCTA />
            </Container>
        </>
    );
};

export default MyClinicWhiteboards;
